// frontend/src/components/ActionPlans/ActionPlanList.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { actionPlansAPI, usersAPI, ap_areasAPI, ap_subAreasAPI, ap_categoriesAPI } from '../../api/client';
import { usePermissions } from '../../context/PermissionsContext';
import { useTranslation } from '../../context/TranslationContext';
import SearchableSelect from '../Common/SearchableSelect';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import { FiPlus, FiX, FiAlertTriangle } from 'react-icons/fi';
import './ActionPlan.css';
import { AP_STATUS_META, getApStatusClass } from '../../constants/statusMeta';

// ── Metadata ──────────────────────────────────────────────────────────────────
const TYPE_META = {
  improvement: { label: 'Melhoria',   color: '#10b981', bg: '#ecfdf5', icon: '🔄' },
  corrective:  { label: 'Corretiva',  color: '#ef4444', bg: '#fee2e2', icon: '🔧' },
  preventive:  { label: 'Preventiva', color: '#f59e0b', bg: '#fffbeb', icon: '🛡️' },
  project:     { label: 'Projeto',    color: '#6366f1', bg: '#eef2ff', icon: '📁' },
  incident:    { label: 'Incidente',  color: '#dc2626', bg: '#fee2e2', icon: '⚠️' },
};
const PRIORITY_META = {
  LOW:      { label: 'Baixa',   color: '#6b7280' },
  MEDIUM:   { label: 'Média',   color: '#f59e0b' },
  HIGH:     { label: 'Alta',    color: '#ef4444' },
  CRITICAL: { label: 'Crítica', color: '#dc2626' },
};

const EMPTY_FILTERS = {
  search: '', status: '', phase: '', priority: '',
  area_id: '', sub_area_id: '', category_id: '', owner: '', overdue: '',
};

// ── Small inline chip ─────────────────────────────────────────────────────────
const Chip = ({ label, color, bg }) => (
  <span style={{ display:'inline-flex', alignItems:'center',
    fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10,
    color, background: bg, whiteSpace:'nowrap', border:`1px solid ${color}30` }}>
    {label}
  </span>
);

// ── Completion mini-bar ───────────────────────────────────────────────────────
const CompletionCell = ({ value }) => {
  const pct = value ?? 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:70 }}>
      <div style={{ flex:1, height:5, background:'var(--gray-100)', borderRadius:3 }}>
        <div style={{ width:`${pct}%`, height:'100%', borderRadius:3,
          background: pct >= 100 ? '#059669' : 'var(--primary)',
          transition:'width 0.2s' }}/>
      </div>
      <span style={{ fontSize:11, fontWeight:700, color:'var(--gray-600)', minWidth:28 }}>
        {pct}%
      </span>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ActionPlanList = () => {
  const { t } = useTranslation();
  const navigate      = useNavigate();
  const routeLocation = useLocation();
  const _rSaved       = routeLocation.state?.aplFilters;
  const { can } = usePermissions();

  const [plans,        setPlans]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [currentPage,  setCurrentPage]  = useState(_rSaved?.page || 1);
  const [itemsPerPage, setItemsPerPage] = useState(_rSaved?.itemsPerPage || 25);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalItems,   setTotalItems]   = useState(0);

  const [filters,      setFilters]      = useState(_rSaved?.filters || EMPTY_FILTERS);
  const [searchInput,  setSearchInput]  = useState(_rSaved?.search  || '');
  const searchDebounce = useRef(null);

  // Dropdown data
  const [users,      setUsers]      = useState([]);
  const [areas,      setAreas]      = useState([]);
  const [subAreas,   setSubAreas]   = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    usersAPI.getAll().then(r => setUsers(r.data.users || r.data.data || [])).catch(() => {});
    ap_areasAPI.getAll().then(r => setAreas(r.data.data || [])).catch(() => {});
    ap_subAreasAPI.getAll().then(r => setSubAreas(r.data.data || [])).catch(() => {});
    ap_categoriesAPI.getAll().then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: itemsPerPage });
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const r = await actionPlansAPI.getAll(params.toString());
      if (r.data.success) {
        setPlans(r.data.data);
        setTotalPages(r.data.pagination?.totalPages ?? 1);
        setTotalItems(r.data.pagination?.totalItems ?? 0);
      }
    } catch {
      setError('Erro ao carregar planos de ação');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setCurrentPage(1); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearchInput(''); setCurrentPage(1); };
  const activeCount = Object.values(filters).filter(Boolean).length;

  // Filtered sub-areas based on selected area
  const visibleSubAreas = filters.area_id
    ? subAreas.filter(s => String(s.area_id) === String(filters.area_id))
    : subAreas;

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      header: 'Referência',
      field: 'reference_no',
      width: 90, minWidth: 80,
      render: row => (
        <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:14,
          color:'var(--gray-700)', background:'var(--gray-100)',
          padding:'1px 6px', borderRadius:4 }}>
          {row.reference_no}
        </span>
      ),
    },
    {
      header: 'Título',
      field: 'title',
      minWidth: 180,
      render: row => (
        <div style={{ maxWidth:320, overflow:'hidden', textOverflow:'ellipsis', fontSize:14,
          whiteSpace:'nowrap', fontWeight:500 }} title={row.title}>
          {row.is_overdue && (
            <FiAlertTriangle size={11} style={{ color:'#dc2626', marginRight:4, flexShrink:0 }}/>
          )}
          {row.title}
        </div>
      ),
    },
    {
      header: 'Estado',
      field: 'status',
      width: 110, minWidth: 90,
      render: row => {
        const sm = (row.status && AP_STATUS_META[row.status]) || AP_STATUS_META.NEW;
        return (
          <span className={`status-badge ${sm?.cls ?? ''}`}>
            {sm?.label ?? row.status ?? '—'}
          </span>
        );
      },
    },
    {
      header: 'Área',
      field: 'area_name',
      width: 120, minWidth: 90,
      render: row => row.area_name
        ? <span style={{ fontSize:14 }}>{row.area_name}</span>
        : <span style={{ color:'var(--gray-300)' }}>—</span>,
    },
    {
      header: 'Sub-Área',
      field: 'sub_area_name',
      width: 120, minWidth: 90,
      render: row => row.sub_area_name
        ? <span style={{ fontSize:14 }}>{row.sub_area_name}</span>
        : <span style={{ color:'var(--gray-300)' }}>—</span>,
    },
    {
      header: 'Eficaz',
      field: 'is_effective',
      width: 80, minWidth: 70,
      render: row => {
        if (row.is_effective === true)
          return <Chip label="✔ Sim" color="#059669" bg="#ecfdf5"/>;
        if (row.is_effective === false)
          return <Chip label="✖ Não" color="#dc2626" bg="#fee2e2"/>;
        return <span style={{ color:'var(--gray-300)' }}>—</span>;
      },
    },
    {
      header: 'Categoria',
      field: 'category_name',
      width: 110, minWidth: 90,
      render: row => row.category_name
        ? <span style={{ fontSize:14 }}>{row.category_name}</span>
        : <span style={{ color:'var(--gray-300)' }}>—</span>,
    },
    {
      header: 'Responsável',
      field: 'owner_name',
      width: 140, minWidth: 110,
      render: row => row.owner_name
        ? <span style={{ fontSize:14 }}>👤 {row.owner_name}</span>
        : <span style={{ color:'var(--gray-300)' }}>—</span>,
    },
    {
      header: 'Conclusão',
      field: 'completion',
      width: 100, minWidth: 80,
      render: row => <CompletionCell value={row.completion}/>,
    },
    {
      header: 'Prazo',
      field: 'target_date',
      width: 95, minWidth: 85,
      render: row => {
        if (!row.target_date) return <span style={{ color:'var(--gray-300)' }}>—</span>;
        const overdue = row.is_overdue;
        return (
          <span style={{ fontSize:14, fontWeight: overdue ? 700 : 400,
            color: overdue ? '#dc2626' : 'var(--gray-600)', whiteSpace:'nowrap' }}>
            {new Date(row.target_date).toLocaleDateString('pt-PT')}
          </span>
        );
      },
    },
    {
      header: 'Tipo',
      field: 'plan_type',
      width: 120, minWidth: 100,
      render: row => {
        const tm = TYPE_META[row.plan_type] || TYPE_META.improvement;
        return <Chip label={`${tm.icon} ${tm.label}`} color={tm.color} bg={tm.bg}/>;
      },
    },
  ];

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className="action-plan-list">

      {/* Header */}
      <div className="page-header page-header--compact">
        <h2>Planos de Ação</h2>
        <div className="page-header-actions">
          {can('action_plans', 'create') && (
            <button className="btn-primary" onClick={() => navigate('/action-plans/new')}>
              <FiPlus size={15} style={{ marginRight:6 }}/> Novo Plano
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="list-filters">
        <div className="filters-row">

          {/* Search */}
          <div className="filter-group filter-group-md">
            <label>Pesquisa</label>
            <div className="filter-search-wrap">
              <input type="text" className="filter-search-input"
                value={searchInput}
                onChange={e => {
                  const val = e.target.value;
                  setSearchInput(val);
                  clearTimeout(searchDebounce.current);
                  searchDebounce.current = setTimeout(() => setF('search', val), 350);
                }}
                placeholder="Referência, título, área…"/>
              {searchInput && (
                <span className="filter-search-clear" onClick={() => {
                  setSearchInput('');
                  setF('search', '');
                }}>
                  <FiX size={12}/>
                </span>
              )}
            </div>
          </div>

          {/* Tipo */}
          <div className="filter-group filter-group-sm">
            <label>Tipo</label>
            <SearchableSelect
              value={filters.phase}
              onChange={v => setF('phase', v)}
              options={Object.entries(TYPE_META).map(([v,m]) => ({ value:v, label:`${m.icon} ${m.label}` }))}
              getOptionLabel={o => o.label} getOptionValue={o => o.value}
              placeholder="Todos"
            />
          </div>

          {/* Estado */}
          <div className="filter-group filter-group-sm">
            <label>Estado</label>
            <SearchableSelect
              value={filters.status}
              onChange={v => setF('status', v)}
              options={Object.entries(AP_STATUS_META).map(([v,m]) => ({ value:v, label:m.label }))}
              getOptionLabel={o => o.label} getOptionValue={o => o.value}
              placeholder="Todos"
            />
          </div>

          {/* Área */}
          <div className="filter-group filter-group-sm">
            <label>Área</label>
            <SearchableSelect
              value={filters.area_id}
              onChange={v => { setF('area_id', v); setF('sub_area_id', ''); }}
              options={areas}
              getOptionLabel={a => a.area_name} getOptionValue={a => a.area_id}
              placeholder="Todas"
            />
          </div>

          {/* Sub-Área */}
          <div className="filter-group filter-group-sm">
            <label>Sub-Área</label>
            <SearchableSelect
              value={filters.sub_area_id}
              onChange={v => setF('sub_area_id', v)}
              options={visibleSubAreas}
              getOptionLabel={s => s.sub_area_name} getOptionValue={s => s.sub_area_id}
              placeholder="Todas"
            />
          </div>

          {/* Categoria */}
          <div className="filter-group filter-group-sm">
            <label>Categoria</label>
            <SearchableSelect
              value={filters.category_id}
              onChange={v => setF('category_id', v)}
              options={categories}
              getOptionLabel={c => c.category_name} getOptionValue={c => c.category_id}
              placeholder="Todas"
            />
          </div>

          {/* Responsável */}
          <div className="filter-group filter-group-md">
            <label>Responsável</label>
            <SearchableSelect
              value={filters.owner}
              onChange={v => setF('owner', v)}
              options={users}
              getOptionLabel={u => u.full_name} getOptionValue={u => u.user_id}
              placeholder="Todos"
            />
          </div>

          {/* Prazo */}
          <div className="filter-group filter-group-xs">
            <label>Prazo</label>
            <select value={filters.overdue} onChange={e => setF('overdue', e.target.value)}>
              <option value="">Todos</option>
              <option value="true">Em atraso</option>
            </select>
          </div>

          {/* Clear */}
          {activeCount > 0 && (
            <div className="filter-group" style={{ justifyContent:'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}
                style={{ alignSelf:'flex-end' }}>
                <FiX size={12}/> Limpar ({activeCount})
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={plans}
        loading={loading}
        error={error}
        onRetry={loadPlans}
        onRowClick={row => navigate(`/action-plans/${row.public_id}`, {
          state: { aplFilters: { filters, search: searchInput,
                                 page: currentPage, itemsPerPage } },
        })}
        emptyMessage="Sem planos de ação."
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={v => { setItemsPerPage(v); setCurrentPage(1); }}
        />
      )}
    </div>
  );
};

export default ActionPlanList;